import React from 'react';
import { Match } from 'effect';
import type { LayeredProxyConfiguration } from '../proxy-types';
import { Separator } from '@/components/ui/separator';
import { getProxyTypeNoun, isProxyTypeEnum } from 'venn-utils';

type ProxyInfo = {
  selectedProxy: { id: string; name: string } | null;
  proxyType: string;
};

type LayerableProxyInfo = ProxyInfo & { extrapolate?: boolean };

interface ExistingProxySectionProps {
  proxyConfiguration: LayeredProxyConfiguration;
}

const getProxyTypeLabel = (proxyType: string, extrapolate: boolean = false) => {
  const proxyTypeLabel = isProxyTypeEnum(proxyType) ? getProxyTypeNoun(proxyType) : proxyType;
  return extrapolate ? `${proxyTypeLabel} + Extrapolate` : proxyTypeLabel;
};

export const ExistingProxySection = ({ proxyConfiguration }: ExistingProxySectionProps) => {
  return Match.value(proxyConfiguration).pipe(
    Match.tag('Empty', () => null),
    Match.tag('SingleLayerable', ({ proxy }) => <SingleProxy proxy={proxy} />),
    Match.tag('SingleNonLayerable', ({ proxy }) => <SingleProxy proxy={proxy} />),
    Match.tag('Multi', ({ firstLayer, secondLayer }) => (
      <ExistingProxyWrapper>
        <Separator className="bg-venn-grey-30" />
        <div className="flex gap-[18px]">
          <ProxyColumn proxy={firstLayer} />
          <ProxyColumn proxy={secondLayer} />
        </div>
      </ExistingProxyWrapper>
    )),
    Match.exhaustive,
  );
};

const ExistingProxyWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col gap-[6px]">
    <div className="font-bold">Existing Proxy</div>
    {children}
  </div>
);

const SingleProxy = ({ proxy }: { proxy: LayerableProxyInfo }) => (
  <ExistingProxyWrapper>
    <div className="min-w-0 overflow-hidden overflow-ellipsis">
      {proxy.selectedProxy?.name ?? ''}{' '}
      <span className="text-[12px] text-venn-grey-80">({getProxyTypeLabel(proxy.proxyType, proxy.extrapolate)})</span>
    </div>
  </ExistingProxyWrapper>
);

const ProxyColumn = ({ proxy }: { proxy: LayerableProxyInfo }) => {
  return (
    <div className="flex flex-col gap-[6px] max-w-[200px]">
      <div className="min-w-0 break-words">{proxy.selectedProxy?.name ?? ''}</div>
      <span className="text-[12px] text-venn-grey-80">
        ({getProxyTypeLabel(proxy.proxyType, proxy.extrapolate ?? false)})
      </span>
    </div>
  );
};
